import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { styled } from '@mui/system';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';

import styles from './styles';
import messages from '../../messages';

const StyledDialog = styled(Dialog)(() => ({
	'& .MuiPaper-root': {
		...styles.dialogPaper,
	},
}));

const LegSelectionDialog = ({
	open,
	handleClose,
	onNext,
	bookingLegs,
	amendmentLegs,
}) => {
	const [selectedLegType, setSelectedLegType] = useState('');

	const outboundSegments = amendmentLegs?.[0].BookingLegTravelSegments || [];
	const inboundSegments = amendmentLegs?.[1].BookingLegTravelSegments || [];

	const handleLegTypeChange = (event) => {
		setSelectedLegType(event.target.value);
	};

	const handleNext = () => {
		if (selectedLegType) {
			onNext({
				value:
					selectedLegType === 'outbound'
						? bookingLegs[0]
						: bookingLegs[1] || {
								// using amendmentLegs and bookingLegs[0] data
								// as there is no bookingLegs[1] for Any time return journeys
								// The Be send only Outbound leg data in those cases
								BookingDetailsLegTicketableFareIds:
									bookingLegs[0].BookingDetailsLegTicketableFareIds,
								departDate: amendmentLegs?.[1].BookingDepartDateTime,
								arriveDate: amendmentLegs?.[1].BookingArriveDateTime,
								arriveStationLongName:
									amendmentLegs?.[1].BookingArriveStationName,
								departStationLongName:
									amendmentLegs?.[1].BookingDepartStationName,
								ticketableFarePrice: bookingLegs[0].ticketableFarePrice,
								ticketableFareCurrency: bookingLegs[0].ticketableFareCurrency,
								BookingLegTravelSegments:
									bookingLegs[0].BookingLegTravelSegments,
								BookingOrderServicesAndAmenities:
									bookingLegs[0].BookingOrderServicesAndAmenities,
								passengers: bookingLegs[0].passengers,
							},
				legSelectionId: selectedLegType === 'outbound' ? 1 : 2,
			});
		}
	};

	const renderLegSection = (title, segments, legType) => (
		<Box sx={styles.legSection}>
			<Box sx={styles.legHeader}>
				<Typography variant="h6" sx={styles.legTitle}>
					{title}
				</Typography>
				<FormControlLabel
					value={legType}
					control={<Radio />}
					labelPlacement="start"
					label={
						legType === 'outbound' ? (
							<FormattedMessage
								{...messages.lblLegSelectionDialogOutboundJourney}
							/>
						) : (
							<FormattedMessage
								{...messages.lblLegSelectionDialogInboundJourney}
							/>
						)
					}
					sx={styles.radioLabel}
				/>
			</Box>
			<Typography variant="body2">
				<FormattedMessage
					{...messages.lblLegSelectionDialogCurrentBookingDetails}
				/>
			</Typography>
			<Box sx={styles.legsContainer}>
				{segments.map((segment, index) => {
					const departureTime =
						segment.BookingTravelSegmentDepartDateTime.split(' ')[1];
					const arrivalTime =
						segment.BookingTravelSegmentArriveDateTime.split(' ')[1];

					return (
						<Paper
							key={segment.BookingTravelSegmentId}
							elevation={1}
							sx={index % 2 === 0 ? styles.leg : styles.legOdd}
						>
							<Box sx={styles.legContent}>
								<Box
									display="flex"
									alignItems="start"
									flexDirection="row"
									gap={1}
								>
									<Box sx={styles.legNumber}>#{index + 1}.</Box>
									<Box sx={styles.legInfo}>
										<Typography variant="body2">
											{segment.BookingTravelSegmentDepartStationName}
										</Typography>
										<Typography variant="body2">
											{segment.BookingTravelSegmentArriveStationName}
										</Typography>
									</Box>
								</Box>
								<Box sx={styles.legTimes}>
									<Typography variant="body2">{departureTime}</Typography>
									<Typography variant="body2">{arrivalTime}</Typography>
								</Box>
							</Box>
						</Paper>
					);
				})}
			</Box>
		</Box>
	);

	return (
		<StyledDialog
			open={open}
			onClose={handleClose}
			disableEnforceFocus
			maxWidth={false}
		>
			<DialogTitle sx={styles.dialogTitle}>
				<div />
				<Button
					variant="contained"
					onClick={handleClose}
					sx={styles.cancelButton}
				>
					<FormattedMessage {...messages.btnCancel} />
				</Button>
			</DialogTitle>
			<DialogContent sx={styles.dialogContent}>
				<RadioGroup
					value={selectedLegType}
					onChange={handleLegTypeChange}
					sx={styles.radioGroup}
				>
					{renderLegSection('Outbound', outboundSegments, 'outbound')}
					{renderLegSection('Inbound', inboundSegments, 'inbound')}
				</RadioGroup>
			</DialogContent>
			<DialogActions sx={styles.dialogActions}>
				<div />
				<Button
					variant="contained"
					onClick={handleNext}
					disabled={!selectedLegType}
					sx={styles.cancelButton}
				>
					<FormattedMessage {...messages.btnNext} />
				</Button>
			</DialogActions>
		</StyledDialog>
	);
};

LegSelectionDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	handleClose: PropTypes.func.isRequired,
	onNext: PropTypes.func.isRequired,
	bookingLegs: PropTypes.arrayOf(
		PropTypes.shape({
			ticketableFarePrice: PropTypes.string,
			ticketableFareCurrency: PropTypes.string,
			departDate: PropTypes.string,
			arriveDate: PropTypes.string,
			departStationLongName: PropTypes.string,
			arriveStationLongName: PropTypes.string,
			BookingDetailsLegTicketableFareIds: PropTypes.string,
			BookingOrderServicesAndAmenities: PropTypes.arrayOf(
				PropTypes.shape({
					BookingOrderServicesAndAmenitiesCode: PropTypes.string,
					BookingOrderServicesAndAmenitiesDesc: PropTypes.string,
				}),
			),
			BookingLegTravelSegments: PropTypes.arrayOf(
				PropTypes.shape({
					BookingLegTravelSegmentId: PropTypes.string,
					BookingLegTravelSegmentDepartDateTime: PropTypes.string,
					BookingLegTravelSegmentArriveDateTime: PropTypes.string,
					BookingOrderDetailsLegTravelSegmentType: PropTypes.string,
					BookingLegTravelSegmentDescription: PropTypes.string,
				}),
			),
			passengers: PropTypes.arrayOf(
				PropTypes.shape({
					passengerId: PropTypes.string,
					firstName: PropTypes.string,
					lastName: PropTypes.string,
				}),
			),
		}),
	).isRequired,
	amendmentLegs: PropTypes.arrayOf(
		PropTypes.shape({
			BookingOrderSeatReservationAmendmentLegCanAdd: PropTypes.bool,
			BookingOrderSeatReservationAmendmentLegCanCancel: PropTypes.bool,
			BookingOrderSeatReservationAmendmentLegCanChange: PropTypes.bool,
			BookingDepartStationCode: PropTypes.string,
			BookingDepartStationName: PropTypes.string,
			BookingDepartStationSupplierCodes: PropTypes.arrayOf(PropTypes.string),
			BookingDepartStationCountryCode: PropTypes.string,
			BookingArriveStationCode: PropTypes.string,
			BookingArriveStationName: PropTypes.string,
			BookingArriveStationSupplierCodes: PropTypes.arrayOf(PropTypes.string),
			BookingArriveStationCountryCode: PropTypes.string,
			BookingDepartDateTime: PropTypes.string,
			BookingArriveDateTime: PropTypes.string,
			BookingTicketableFareIds: PropTypes.arrayOf(PropTypes.string),
			BookingLegId: PropTypes.string,
			BookingLegTravelSegments: PropTypes.arrayOf(
				PropTypes.shape({
					BookingTravelSegmentArriveDateTime: PropTypes.string,
					BookingTravelSegmentArriveStationCode: PropTypes.string,
					BookingTravelSegmentArriveStationName: PropTypes.string,
					BookingTravelSegmentDepartDateTime: PropTypes.string,
					BookingTravelSegmentDepartStationCode: PropTypes.string,
					BookingTravelSegmentDepartStationName: PropTypes.string,
					BookingTravelSegmentId: PropTypes.string,
				}),
			),
		}),
	).isRequired,
};

export default injectIntl(LegSelectionDialog);
